import React, { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { CheckOutlined, MailOutlined } from '@ant-design/icons';
import { Spin, Typography } from 'antd';
import styled from 'styled-components';

import { getMailboxConnectionActionItems } from 'apis/ActionCenterAPI';
import { retryShopperEmailMailboxSync } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import { ACTION_CENTER_QUERY_KEY, MAILBOX_CONNECTION_ACTION } from 'constants/actionCenter';
import { getErrorMessageByPriority } from 'helpers/emails';
import { useAppSelector } from 'store/hook';
import { MailboxConnectionActionItemType } from 'types/actionCenter';

import ActionCenterItem from './ActionCenterItem';
import { useShopperEmailsMailboxActions } from '../MysteryShopping/ShopperEmails/useShopperEmailsMailboxActions';

const ACTION_CENTER_REFRESH_INTERVAL_MS = 15_000;

const LoadingContainer = styled.div`
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 8px;
  color: var(--text-secondary);
`;

const SectionTitle = styled(Typography.Text)`
  font-family: var(--font-bold);
`;

const EmptyState = styled.div`
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  color: var(--text-secondary);
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--positive-green-light);
  color: var(--positive-green);
  font-size: 20px;
`;

type Props = {
  enabled: boolean;
};

const ActionCenterItems = ({ enabled }: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [retryingEmailId, setRetryingEmailId] = useState<number | null>(null);
  const { mutateAsync: retryMailboxSync } = useMutation({
    mutationFn: retryShopperEmailMailboxSync,
  });
  const {
    data: mailboxActions,
    isError: mailboxActionsError,
    isLoading: mailboxActionsLoading,
    refetch: refetchMailboxActions,
  } = useQuery({
    queryKey: [ACTION_CENTER_QUERY_KEY.ROOT, ACTION_CENTER_QUERY_KEY.MAILBOX_CONNECTIONS, currentUser?.user_id],
    queryFn: getMailboxConnectionActionItems,
    enabled,
    refetchInterval: enabled ? ACTION_CENTER_REFRESH_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
  });

  const refreshActionCenter = useCallback(async () => {
    await refetchMailboxActions();
    await queryClient.invalidateQueries([ACTION_CENTER_QUERY_KEY.ROOT, ACTION_CENTER_QUERY_KEY.SUMMARY]);
  }, [queryClient, refetchMailboxActions]);

  const { authenticatingEmailId, handleMailboxAction } = useShopperEmailsMailboxActions(refreshActionCenter);

  const handleAction = async (item: MailboxConnectionActionItemType) => {
    if (item.action !== MAILBOX_CONNECTION_ACTION.RETRY_SYNC) {
      await handleMailboxAction({ id: item.shopper_contact_id });
      return;
    }

    setRetryingEmailId(item.shopper_contact_id);
    try {
      const response = await retryMailboxSync(item.shopper_contact_id);
      showMessage('success', response?.detail ?? 'Mailbox synchronization retry scheduled.');
    } catch (error: any) {
      showMessage(
        'error',
        getErrorMessageByPriority(error?.response?.data, 'Failed to retry mailbox synchronization.', ['detail'])
      );
    } finally {
      setRetryingEmailId(null);
    }
  };

  if (mailboxActionsLoading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    );
  }

  return (
    <>
      <SectionHeader>
        <MailOutlined />
        <SectionTitle>Email connections</SectionTitle>
      </SectionHeader>
      {mailboxActionsError ? (
        <EmptyState role="alert">Unable to load pending actions.</EmptyState>
      ) : mailboxActions?.results.length ? (
        mailboxActions.results.map((item: MailboxConnectionActionItemType) => (
          <ActionCenterItem
            key={item.id}
            item={item}
            loading={authenticatingEmailId === item.shopper_contact_id || retryingEmailId === item.shopper_contact_id}
            onAction={handleAction}
          />
        ))
      ) : (
        <EmptyState>
          <EmptyStateIcon>
            <CheckOutlined />
          </EmptyStateIcon>
          No pending actions.
        </EmptyState>
      )}
    </>
  );
};

export default ActionCenterItems;
